import { Injectable, inject } from '@angular/core';
import {
  ExportContext,
  ExportFileFormat,
  ExportRange,
  ExportService,
} from 'app/layout/export-overlay';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import ceil from 'lodash-es/ceil';
import { takeUntil } from 'rxjs';
import { BusinessPartnerBySubRoute } from '../../../interfaces/busines-partner-by-sub-route.interface';
import { UnassignedPartnersSidebarService } from '../unassigned-partners-sidebar.service';
import {
  UNASSIGNED_PARTNERS_EXPORT_ID,
  UNASSIGNED_PARTNERS_LOCALIZED_PROPERTIES,
  unassignedPartnersExportTitle,
} from './unassigned-partners.constants';

@Injectable()
export class UnassignedPartnersExport {
  private exportService = inject(ExportService);
  private businessPartnerService = inject(BusinessPartnerService);
  private sidebarService = inject(UnassignedPartnersSidebarService);

  private transformItem(item: BusinessPartnerBySubRoute): Record<string, unknown> {
    return {
      name: item.name ?? '',
      externalCode: item.externalKeys?.code ?? '',
      address: item.principalAddress?.address ?? '',
      contact: item.principalContact?.fullName ?? '',
      phone: item.principalPhone
        ? `${item.principalPhone.prefix ?? ''}-${item.principalPhone.phone ?? ''}`
        : '',
      email: item.principalContact?.emails?.[0]?.email ?? '',
      assignedRoutes: item.subRoutes?.length ?? 0,
    };
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number
  ): void {
    const subRouteId = this.sidebarService.subRouteId();
    const searchTerm = this.sidebarService.searchTerm();

    if (!subRouteId) return;

    const totalPages = ceil(totalItems / pageSize) + 1;

    this.exportService
      .task({
        id: UNASSIGNED_PARTNERS_EXPORT_ID,
        title: unassignedPartnersExportTitle(),
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            unassignedPartnersExportTitle(),
            UNASSIGNED_PARTNERS_LOCALIZED_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const page = range === ExportRange.AllPages ? ctx.iteration : pageIndex + 1;

          this.businessPartnerService
            .findUnassignedToSubRouteBusinessPartnerWithCount(
              {},
              {
                currentPage: page,
                limitPerPage: pageSize,
                search: searchTerm,
                sort: 'name',
                sortDirection: 'ASC',
              },
              subRouteId,
              this.sidebarService.subRouteData()?.company?._id?.toString() ?? null
            )
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              response.data.data.forEach((item) => {
                ctx.processor.addToSection(
                  unassignedPartnersExportTitle(),
                  this.transformItem(item)
                );
              });
              ctx.next();
            });
        },
      })
      .start();
  }

  canStartTask(): boolean {
    if (this.exportService.taskIsActive(UNASSIGNED_PARTNERS_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}
